/* eslint-disable */
import React, { useState } from 'react';
import { useRouteLoaderData, useLoaderData, useParams } from 'react-router-dom';
import { Event, Registration } from './API';
import AttendeeList from './AttendeeList';
import ScanTicket from './ScanTicket';
import EventQr from './EventQr';
import FileUpload from "./FileUpload";
import NavBar from './NavBar';
import AddUser from './AddUserModal';
import AddGuestModal from './AddGuestModal';
import { Button } from '@uclaea/react-component-library';
import ZplPrinter from './components/ZplPrinter';
import PrintSettings from './components/PrintSettings';
import './css/Event.css';
import Switch from 'react-switch';
import { isAdmin, isTeamMember } from './constants/roles';
import { usePrinting } from './hooks/usePrinting';
import { useRegistrationSubscriptions } from './hooks/useRegistrationSubscriptions';

const EventItem: React.FC = () => {
  // Fetch data from loaders
  const events = useRouteLoaderData("events") as Event[];
  const initialRegistrations = useLoaderData() as Registration[];
  const { eventId } = useParams();

  // UI state
  const [scanTicket, setScanTicket] = useState(false);
  const [viewQr, setViewQr] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddGuestModal, setShowAddGuestModal] = useState(false);
  const [userFeedback, setUserFeedback] = useState<string | null>(null);
  const [guestFeedback, setGuestFeedback] = useState<string | null>(null);

  // Custom hooks
  const {
    printingEnabled, pendingPrintAttendee, setPendingPrintAttendee,
    zplFeedback, printConfig, setPrintConfig,
    handleTogglePrinting, handlePrinterReady, handlePrintResult,
  } = usePrinting();

  const { registrations, setRegistrations } = useRegistrationSubscriptions(
    eventId, initialRegistrations, printingEnabled, setPendingPrintAttendee
  );

  // Get current event
  const event = events.find(e => e.eventId === eventId);
  if (!eventId || !event) return <div>Not Found</div>;

  const { eventName, location, date, description, eventRole } = event;

  // Event Handlers
  const startScanningTicket = () => setScanTicket(!scanTicket);
  const handleAddUserClick = () => setShowAddUserModal(true);
  const handleCloseAddUserModal = () => setShowAddUserModal(false);
  const readableDate = new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const handleAddUserSuccess = () => {
    setUserFeedback('User added to event team successfully!');
    setTimeout(() => setUserFeedback(null), 4000);
    setShowAddUserModal(false);
  };
  const handleAddUserError = () => {
    setUserFeedback('Failed to add user to event team.');
    setTimeout(() => setUserFeedback(null), 4000);
  };

  return scanTicket ? (
    <ScanTicket
      eventId={eventId}
      handler={() => null} // Adjust based on scan handling requirements
      goBack={() => setScanTicket(false)}
      attendees={registrations}
      setAttendees={setRegistrations}
    />
  ) : viewQr ? (
    <EventQr event={event} setViewQr={setViewQr} />
  ) : (
    <div className="event-details">
      <NavBar eventId={eventId} eventName={eventName} />
      <h1 className="event-details-header">
        Event Details for <br />
        <b>{eventName}</b>
      </h1>
      <div className="event-meta">
        <span className="event-meta-location">{location}</span>
        <span className="event-meta-date">{readableDate}</span>
      </div>
      <p className="event-description">{description}</p>

      {/* Action Buttons and Feedback */}
      <div className="event-actions-group">
        {/* Row 1: Scan Tickets full width */}
        <div className="event-actions-row event-actions-row--full">
          <Button
            variant="primary"
            label="Scan Tickets"
            className="event-button event-button--full"
            onClick={startScanningTicket}
            disabled={!registrations.length}
          />
        </div>
        {/* Row 2: Add Users and Add Guest split evenly */}
        <div className="event-actions-row event-actions-row--split">
          {isAdmin(eventRole) && (
            <Button
              variant="primary"
              label="Add Users To Event Team"
              className="event-button event-button--half"
              onClick={handleAddUserClick}
            />
          )}
          {isTeamMember(eventRole) && (
            <Button
              variant="primary"
              label="Add Guest"
              className="event-button event-button--half"
              onClick={() => setShowAddGuestModal(true)}
            />
          )}
        </div>
        {/* Feedback messages */}
        {userFeedback && <div className={`event-actions-feedback ${userFeedback.includes('success') ? 'feedback--success' : 'feedback--error'}`}>{userFeedback}</div>}
        {guestFeedback && <div className={`event-actions-feedback ${guestFeedback.includes('success') ? 'feedback--success' : 'feedback--error'}`}>{guestFeedback}</div>}
        {/* Row 3: FileUpload centered below */}
        {isAdmin(eventRole) && (
          <>
            <div className="event-actions-row event-actions-row--center">
              <FileUpload eventId={eventId} />
            </div>
            <div className="event-actions-row event-actions-row--center printing-toggle-row">
              <div className="printing-toggle-inner">
                <span className="printing-toggle-label">Enable Printing</span>
                <Switch
                  checked={printingEnabled}
                  onChange={handleTogglePrinting}
                  color="primary"
                />
              </div>
              <PrintSettings settings={printConfig} onApply={setPrintConfig} />
            </div>
            {zplFeedback && (
              <div className={`event-actions-feedback ${zplFeedback.includes('error') ? 'feedback--error' : 'feedback--success'}`}>{zplFeedback}</div>
            )}
            {/* ZplPrinter is headless, used for setup and printing */}
            {printingEnabled && (
              <>
                <ZplPrinter
                  attendee={pendingPrintAttendee}
                  eventInfo={event}
                  printConfig={printConfig}
                  onPrinterReady={handlePrinterReady}
                  onPrintResult={handlePrintResult}
                />
                <div className='event-actions-row event-actions-row--full'>
                  <p>*Printing Enabled. Do not refresh or navigate away from this page while printing is enabled.</p>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Add Guest Modal */}
      {isTeamMember(eventRole) && showAddGuestModal && (
        <AddGuestModal
          eventId={eventId}
          onClose={() => setShowAddGuestModal(false)}
          onSuccess={() => {
            setGuestFeedback('Guest added successfully!');
            setTimeout(() => setGuestFeedback(null), 7000);
          }}
        />
      )}

      <AttendeeList attendees={registrations} setAttendees={setRegistrations} />

      {showAddUserModal && (
        <AddUser eventDetails={event} onClose={handleCloseAddUserModal} onSuccess={handleAddUserSuccess} onError={handleAddUserError} />
      )}
    </div>
  );
};

export default EventItem;
